import { createContext, useContext, useMemo } from 'react';
import { NotificationHandlerWrapper } from '../lib/notificationWrapper';
import type { ApiHandlerContract } from '../types/handlers/api';
import type { AuthHandlerContract } from '../types/handlers/auth';
import type { NavigationHandlerContract } from '../types/handlers/navigation';
import type {
  NotificationHandlerContract,
  WrappedNotificationHandlerContract,
} from '../types/handlers/notification';
import type { BillingPlanKey, Platform } from './env-context';

export type SiteType = {
  siteId: string;
  siteEnv: string;
  teamId?: number;
  userId?: number;
  userEmail?: string;
  userName?: string;
  isSparkStaff?: boolean;
  plan?: BillingPlanKey;
  platform?: Platform;
};

// Apis extend this via interface merging so each api self-registers its contract.
// biome-ignore lint/suspicious/noEmptyInterface: extended via interface merging per-api
export interface Apis {}

export type BuildApis = (apiHandler: ApiHandlerContract) => Apis;

// `buildApis` is supplied rather than defaulted: this package holds no feature apis, so the
// package that owns the pages being mounted decides what goes in.
export type HandlersInput = {
  apiHandler: ApiHandlerContract;
  navigationHandler: NavigationHandlerContract;
  notificationHandler: NotificationHandlerContract;
  authHandler: AuthHandlerContract;
  buildApis: BuildApis;
};

export type HandlersType = {
  navigationHandler: NavigationHandlerContract;
  notificationHandler: WrappedNotificationHandlerContract;
  authHandler: AuthHandlerContract;
  apis: Apis;
};

export type UserType = {
  firstName?: string;
  isSparkStaff?: boolean;
  manageAccount?: boolean;
  updateBilling?: boolean;
  twoFactorEnabled?: boolean;
  twoFactorRequired?: boolean;
};

export type PageContextType = {
  site: SiteType;
  handlers: HandlersType;
  user?: UserType;
};

function resolveHandlers(input: HandlersInput): HandlersType {
  return {
    navigationHandler: input.navigationHandler,
    notificationHandler: NotificationHandlerWrapper(input.notificationHandler),
    authHandler: input.authHandler,
    apis: input.buildApis(input.apiHandler),
  };
}

export const PageContext = createContext<PageContextType | undefined>(undefined);

// Make `pageProps` optional when the page declares no props, so callers don't have to
// pass an empty `{}` just to satisfy the type. Required otherwise.
export type WithPageProps<TBase, TPageProps extends Record<string, unknown>> = TBase &
  // biome-ignore lint/complexity/noBannedTypes: `{}` is the empty-object check that makes pageProps optional when TPageProps has no required keys
  ({} extends TPageProps ? { pageProps?: TPageProps } : { pageProps: TPageProps });

// Memoize the context value so PageContext consumers don't re-render when the caller
// passes fresh `site`/`handlers` object literals with identical contents.
export function usePageContextValue(
  site: SiteType,
  handlers: HandlersInput,
  user?: UserType,
): PageContextType {
  return useMemo(
    () => ({ site, user, handlers: resolveHandlers(handlers) }),
    [
      site.siteId,
      site.siteEnv,
      site.teamId,
      site.userId,
      site.userEmail,
      site.userName,
      site.isSparkStaff,
      site.plan,
      site.platform,
      user?.firstName,
      user?.isSparkStaff,
      user?.manageAccount,
      user?.updateBilling,
      user?.twoFactorEnabled,
      user?.twoFactorRequired,
      handlers.apiHandler,
      handlers.navigationHandler,
      handlers.notificationHandler,
      handlers.authHandler,
      handlers.buildApis,
    ],
  );
}

export function useSite(): SiteType {
  const context = useContext(PageContext);
  if (!context) {
    throw new Error('useSite must be used within a PageProvider');
  }
  return context.site;
}

export function useUser(): UserType {
  const context = useContext(PageContext);
  return context?.user ?? {};
}

export function useHandlers(): HandlersType {
  const context = useContext(PageContext);
  if (!context) {
    throw new Error('useHandlers must be used within a PageProvider');
  }
  return context.handlers;
}
